import React, { useState } from "react";
import API from "../../services/api";
import { nzToday } from "../runsheets/util";

/* Shared pieces for the Trucks tab and the truck card in Settings: the three
 * expiries on the paper header, the check status chip, the faulted-item chips
 * (with the driver's note on each), the Faults/Comments block, the "action
 * taken" form the office fills in for a fault, and the "remove" form that
 * voids a check - never deletes it.
 */

// The header of NZTA's Daily Checklist prints these three, in this order.
export const EXPIRY_FIELDS = [
  { key: "cof_expiry", label: "COF" },
  { key: "licence_expiry", label: "Rego" },
  // RUC runs out at a hubo reading, not on a date - the server measures it
  // against the truck's latest pre-check hubo
  { key: "ruc_expiry_km", label: "RUC", km: true },
  // the service date rides the same warning rule as the documents
  { key: "next_service_date", label: "Service" },
];

// How far ahead an expiry turns amber. Matches the server's expiry_warnings
// window so the office never sees a chip the API disagrees with.
export const DUE_DAYS = 30;

// The items the office wants in red when they fail: a mirror fault is a
// nuisance, a brake fault is the truck not going out. Same list as the
// server's `critical` flag on /checks/template; kept here so the highlight
// does not depend on an extra request before the page can render.
export const CRITICAL_KEYS = new Set([
  "tyres_wheels", "steering",
  "park_brake", "brake_pedal_feel", "leaks_air_fluid", "brake_operation",
  "front_lights", "rear_lights",
]);

// The paper labels, in paper order - the server's CHECK_GROUPS. A driver's
// note on an item that was NOT crossed ("tyre wearing, still OK") arrives as
// item_notes[key] with no label beside it; the office reads "Tyres / Wheels:
// tyre wearing" here without a round trip to /checks/template first.
export const ITEM_LABELS = {
  engine_oil: "Engine Oil", power_steering: "Power Steering", cooling_water: "Cooling Water",
  load_security: "Load Security", load_anchors: "Load Anchors", mirrors: "Mirrors",
  doors_locks: "Doors / Locks", mudguards_flaps: "M.Guards/Flaps", drain_air_tanks: "Drain Air Tanks",
  tyres_wheels: "Tyres / Wheels", body_deck_security: "Body/Deck Security", tow_connections: "Tow Connections",
  cab_release_lock: "Cab Release / Lock", fuel_tank_cap: "Fuel Tank / Cap",
  front_lights: "All Front Lights", rear_lights: "All Rear Lights", side_mark_lights: "Side Mark Lights",
  other_lights: "Other Lights",
  windscreen: "Windscreen", washers_wipers: "Washers/Wipers", low_air_buzzer: "Low Air Buzzer",
  gauges: "All Gauges", seat_seatbelt: "Seat / Seat Belt", horn: "Horn", steering: "Steering",
  park_brake: "Park Brake", brake_pedal_feel: "Brake Pedal Feel", leaks_air_fluid: "Leaks-Air/Fluid",
  brake_operation: "Operation",
};
const ITEM_ORDER = Object.keys(ITEM_LABELS);

// The check's item_notes as a plain object, whatever build sent it.
const notesOf = (check) =>
  (check?.item_notes && typeof check.item_notes === "object" && !Array.isArray(check.item_notes))
    ? check.item_notes : {};

// The driver's notes on items that were NOT crossed. Crossed items carry
// their note on faults[].note and show it on the chip; these are the rest,
// in paper order, so "still OK but watch it" reaches the office too.
export const extraItemNotes = (check) => {
  const notes = notesOf(check);
  const faulted = new Set((Array.isArray(check?.faults) ? check.faults : []).map((f) => f.key));
  const pos = (k) => { const i = ITEM_ORDER.indexOf(k); return i === -1 ? ITEM_ORDER.length : i; };
  return Object.keys(notes)
    .filter((k) => String(notes[k] || "").trim() && !faulted.has(k))
    .sort((a, b) => pos(a) - pos(b))
    .map((k) => ({ key: k, label: ITEM_LABELS[k] || k, note: String(notes[k]).trim() }));
};

const dayNum = (d) => Math.floor(Date.parse(`${String(d).slice(0, 10)}T00:00:00Z`) / 86400000);

// What the office needs next to an expiry date: nothing, "due" (amber) or
// "expired" (red). The server's expiry_warnings is the authority; the local
// date comparison only covers an API build that does not send them yet, so
// an expired COF is never shown as a plain date.
export function expiryState(truck, field, today = nzToday()) {
  // km fields (RUC): the server decides, against the latest hubo
  if (field.endsWith("_km")) {
    const limit = truck?.[field];
    if (!limit) return { date: "", state: "none", days: null, km: true };
    const warn = (truck.expiry_warnings || []).find((w) => w.field === field);
    const state = warn ? (warn.level === "expired" ? "expired" : "due") : "ok";
    return { date: `${Number(limit).toLocaleString("en-NZ")} km`, state, days: null, km: true,
             kmLeft: warn?.km_left ?? null };
  }
  const date = truck?.[field] ? String(truck[field]).slice(0, 10) : "";
  if (!date) return { date: "", state: "none", days: null };
  const warn = (truck.expiry_warnings || []).find((w) => w.field === field);
  let days = warn && typeof warn.days === "number" ? warn.days : dayNum(date) - dayNum(today);
  if (Number.isNaN(days)) days = null;
  let state = "ok";
  if (days != null && days < 0) state = "expired";
  else if (warn || (days != null && days <= DUE_DAYS)) state = "due";
  return { date, state, days };
}

export const EXPIRY_CLASS = {
  expired: "text-red-300 border-red-500/40 bg-red-500/10",
  due: "text-amber-300 border-amber-500/40 bg-amber-500/10",
  ok: "text-slate-300 border-slate-700 bg-slate-800/40",
  none: "text-slate-600 border-slate-800 bg-transparent",
};

export function ExpiryChip({ truck, field, label }) {
  const { date, state, days, km, kmLeft } = expiryState(truck, field);
  let tail = "";
  if (km) {
    if (state === "expired") tail = kmLeft != null ? ` · ran out ${Math.abs(kmLeft).toLocaleString("en-NZ")} km ago` : " · ran out";
    else if (state === "due") tail = kmLeft != null ? ` · ${kmLeft.toLocaleString("en-NZ")} km left` : " · nearly out";
  } else if (state === "expired") tail = days === -1 ? " · expired yesterday" : ` · expired ${Math.abs(days)} d ago`;
  else if (state === "due") tail = days === 0 ? " · due today" : ` · due in ${days} d`;
  return (
    <span className={`inline-block px-2 py-0.5 rounded border text-[10px] font-semibold whitespace-nowrap ${EXPIRY_CLASS[state]}`}
      title={`${label} expiry`}>
      {label} {date || "—"}{tail}
    </span>
  );
}

export const STATUS_META = {
  ok: { label: "OK", cls: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" },
  fault: { label: "FAULT", cls: "text-red-300 border-red-500/40 bg-red-500/10" },
  cleared: { label: "CLEARED", cls: "text-sky-300 border-sky-500/40 bg-sky-500/10" },
};

export function StatusChip({ status }) {
  const m = STATUS_META[status] || { label: String(status || "—").toUpperCase(), cls: "text-slate-300 border-slate-600 bg-slate-700/40" };
  return (
    <span className={`inline-block px-2 py-0.5 rounded border text-[10px] font-bold tracking-wide whitespace-nowrap ${m.cls}`}>
      {m.label}
    </span>
  );
}

// The faulted items by their paper label. Safety-critical ones in red so the
// office can tell "mirror" from "brakes" without opening the check. A fault
// with the driver's note reads "<label> - <note>" on its own line, so what
// is actually wrong is on the row and not behind a click. `notes` is the
// check's item_notes, the fallback for a build whose faults[] carry no note.
export function FaultChips({ faults, notes }) {
  const list = Array.isArray(faults) ? faults : [];
  if (!list.length) return <span className="text-slate-600">—</span>;
  const fallback = notes && typeof notes === "object" ? notes : {};
  return (
    <div className="flex flex-wrap items-center gap-1">
      {list.map((f) => {
        const note = String(f.note || fallback[f.key] || "").trim();
        return (
          <span key={f.key} className={`inline-flex flex-wrap items-baseline gap-x-1.5 ${note ? "basis-full" : ""}`}>
            <span
              className={`inline-block px-1.5 py-0.5 rounded-full border text-[9px] font-semibold uppercase tracking-wide whitespace-nowrap ${
                CRITICAL_KEYS.has(f.key)
                  ? "text-red-300 border-red-500/40 bg-red-500/10"
                  : "text-amber-300 border-amber-500/40 bg-amber-500/10"}`}>
              {f.label || f.key}
            </span>
            {note && <span className="text-slate-300 font-normal">- {note}</span>}
          </span>
        );
      })}
    </div>
  );
}

// The Faults/Comments line as the office reads it: the driver's notes on
// un-crossed items first ("<Item>: <note>"), then the free text with its
// line breaks kept - the office's own comments are appended to it as
// "HH:MM name: ..." lines and must not run together.
export function CheckComments({ check, prefix, empty = "—" }) {
  const notes = extraItemNotes(check);
  const text = String(check?.faults_note || "").trim();
  if (!notes.length && !text) return empty == null ? null : <span className="text-slate-600">{empty}</span>;
  return (
    <div className="text-slate-300">
      {prefix && <span className="text-slate-500">{prefix} </span>}
      <span className="inline-block align-top space-y-0.5">
        {notes.map((n) => (
          <div key={n.key}><span className="text-slate-500">{n.label}:</span> {n.note}</div>
        ))}
        {text && <div className="whitespace-pre-wrap">{text}</div>}
      </span>
    </div>
  );
}

// The audit line under a removed check. An auditor reads who and why on the
// row itself - a removed check is never a gap in the sheet.
export function VoidedLine({ check }) {
  return (
    <div className="text-[10px] text-slate-500 italic">
      removed by <span className="text-slate-400 not-italic">{check.voided_by || "—"}</span>
      {" · "}<span className="not-italic">{check.void_reason || "no reason recorded"}</span>
      {check.voided_at_nz ? <span className="not-italic"> · {check.voided_at_nz}</span> : null}
    </div>
  );
}

// The control that opens the remove form. Quiet until hovered: it sits on
// every row and must not read as the row's main action.
export function RemoveButton({ open, onClick }) {
  return (
    <button type="button" onClick={onClick} title="Remove this check"
      className={`px-2 py-1 rounded-lg border text-[10px] font-semibold whitespace-nowrap transition-all ${
        open
          ? "text-red-200 border-red-500/40 bg-red-500/15"
          : "text-slate-500 border-slate-800 hover:text-red-300 hover:border-red-500/40"}`}>
      {open ? "Close" : "Remove"}
    </button>
  );
}

// "Mon 14/09" - the history table has a column per check and no room for the
// long form. check_date is already the NZ day; no timezone maths here.
export const shortDate = (d) => {
  if (!d) return "—";
  try {
    const dt = new Date(`${String(d).slice(0, 10)}T00:00:00`);
    if (Number.isNaN(dt.getTime())) return String(d).slice(0, 10);
    return dt.toLocaleDateString("en-NZ", { weekday: "short", day: "2-digit", month: "2-digit" });
  } catch { return String(d).slice(0, 10); }
};

export const fmtHubo = (n) => (n == null || n === "" ? "—" : Number(n).toLocaleString("en-NZ"));

// Newest first: the day the check was done, then the moment it was
// submitted. Both are ISO strings, so a plain string compare orders them.
export const sortNewest = (checks) =>
  (Array.isArray(checks) ? checks.slice() : []).sort((a, b) =>
    `${b.check_date || ""} ${b.submitted_at || ""}`.localeCompare(`${a.check_date || ""} ${a.submitted_at || ""}`));

// Which of the fleet an operator should see by default. A retired truck keeps
// its record (its checks are kept for years) but should not clutter the list.
export const isActiveTruck = (t) => t.is_active !== false && t.is_active !== 0;

/* The mechanic's line on the paper form: "Action Taken (Mechanic Sign) /
 * Date". Recording it is what turns a red truck back to normal - the check
 * itself is never edited or deleted, the action is added to it. */
export function ActionForm({ check, onDone, onCancel, reportError }) {
  const [actionTaken, setActionTaken] = useState("");
  const [actionBy, setActionBy] = useState(() => {
    try { return localStorage.getItem("dav_username") || ""; } catch { return ""; }
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (!actionTaken.trim()) return;
    setBusy(true);
    try {
      const res = await API.patch(`/checks/${check.id}/action`, {
        action_taken: actionTaken.trim(),
        action_by: actionBy.trim() || undefined,
      });
      await onDone(res.data?.check || res.data);
    } catch (err) {
      reportError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-slate-950/60 border border-slate-800 rounded-xl p-3 space-y-2">
      <p className="text-[10px] text-slate-400">
        What was done about this fault. The check keeps its fault record; this is the
        "Action Taken" line on the form, signed by whoever records it.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        <textarea value={actionTaken} onChange={(e) => setActionTaken(e.target.value)} rows={2}
          placeholder="e.g. Replaced nearside mirror glass, road tested OK"
          className="md:col-span-2 bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-xs text-slate-200 outline-none focus:border-sky-500" />
        <label className="text-[10px] text-slate-400">
          Signed by
          <input value={actionBy} onChange={(e) => setActionBy(e.target.value)} placeholder="Mechanic / office"
            className="mt-1 w-full bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-xs text-slate-200 outline-none focus:border-sky-500" />
        </label>
        <div className="flex items-end gap-2">
          <button type="submit" disabled={busy || !actionTaken.trim()}
            className="flex-1 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white text-xs font-semibold rounded-lg px-3 py-2">
            {busy ? "Saving…" : "Save action"}
          </button>
          <button type="button" onClick={onCancel}
            className="px-3 py-2 rounded-lg text-xs bg-slate-800 text-slate-300 hover:bg-slate-700">
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
}

/* "Remove" on the office console is a VOID, not a delete. The row stays,
 * marked with who removed it and why, and every screen and print hides it -
 * that is how a dummy test check disappears without leaving a gap in years
 * of records. The reason is required: an auditor must read a sentence, not
 * a blank. */
export const VOID_REASON_MIN = 3;
export const VOID_REASON_MAX = 255;

export function VoidForm({ check, onDone, onCancel, reportError }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const trimmed = reason.trim();
  const valid = trimmed.length >= VOID_REASON_MIN && trimmed.length <= VOID_REASON_MAX;

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (!valid) return;
    setBusy(true);
    try {
      const res = await API.post(`/checks/${check.id}/void`, { reason: trimmed });
      await onDone(res.data?.check || res.data);
    } catch (err) {
      reportError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-slate-950/60 border border-red-500/30 rounded-xl p-3 space-y-2">
      <p className="text-xs font-bold text-red-200">Remove this check</p>
      <p className="text-[10px] text-slate-400">
        It disappears from every screen and print. The record is kept, marked removed by you with this reason.
      </p>
      <div className="flex flex-col md:flex-row gap-2">
        <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={VOID_REASON_MAX}
          placeholder="Why - e.g. test entry, wrong truck, submitted twice"
          className="flex-1 bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-xs text-slate-200 outline-none focus:border-red-500" />
        <div className="flex items-center gap-2">
          <button type="submit" disabled={busy || !valid}
            className="bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-xs font-semibold rounded-lg px-3 py-2 whitespace-nowrap">
            {busy ? "Removing…" : "Remove check"}
          </button>
          <button type="button" onClick={onCancel}
            className="px-3 py-2 rounded-lg text-xs bg-slate-800 text-slate-300 hover:bg-slate-700">
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
}

// One row per open fault, used both fleet-wide and inside a truck's history.
// `form` is which inline form is open under this row: null, "action" or
// "void" - one at a time, so the office cannot clear and remove in one go.
export function FaultRow({ check, showTruck, onOpenTruck, form, onToggleForm, onDone, onVoided, onCancel, reportError }) {
  return (
    <div className="rounded-xl border border-red-500/30 bg-red-500/5 px-3 py-2 space-y-2 text-xs">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {showTruck && (
          <button onClick={() => onOpenTruck && onOpenTruck(check.truck_id)}
            className="font-extrabold text-slate-100 hover:text-white underline decoration-slate-600 hover:decoration-white"
            title="Open this truck's history">
            {check.truck_plate || check.truck_id}
          </button>
        )}
        <span className="text-slate-300">{shortDate(check.check_date)}{check.submitted_nz ? ` ${check.submitted_nz}` : ""}</span>
        <span className="text-slate-600">|</span>
        <span className="font-semibold text-slate-200">{check.person_name || "—"}</span>
        {check.hubo_reading != null && (
          <>
            <span className="text-slate-600">|</span>
            <span className="text-slate-400 font-mono">hubo {fmtHubo(check.hubo_reading)}</span>
          </>
        )}
        {check.critical_fault && (
          <span className="px-1.5 py-0.5 rounded border text-[9px] font-bold uppercase tracking-wide text-red-200 border-red-500/60 bg-red-500/20">
            safety critical
          </span>
        )}
        <span className="ml-auto flex items-center gap-2">
          <StatusChip status={check.status} />
          <button onClick={() => onToggleForm("action")}
            className="text-sky-300 hover:text-sky-200 px-2 py-1 bg-sky-500/10 hover:bg-sky-500/20 border border-sky-500/25 rounded-lg transition-all font-semibold whitespace-nowrap">
            {form === "action" ? "Close" : "Record action taken"}
          </button>
          <RemoveButton open={form === "void"} onClick={() => onToggleForm("void")} />
        </span>
      </div>
      {/* The item notes travel with the chips, so the office reads what is
        * wrong here without opening the truck. */}
      <FaultChips faults={check.faults} notes={check.item_notes} />
      <CheckComments check={check} prefix="Comments:" empty={null} />
      {form === "action" && (
        <ActionForm check={check} onDone={onDone} onCancel={onCancel} reportError={reportError} />
      )}
      {form === "void" && (
        <VoidForm check={check} onDone={onVoided} onCancel={onCancel} reportError={reportError} />
      )}
    </div>
  );
}
